import BlockUi from '@availity/block-ui';
import pointsWithinPolygonFn from '@turf/points-within-polygon';
import { ChartEvent, ChartOptions, ChartType, LegendElement, LegendItem, TooltipItem } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import { CSVLink } from 'react-csv';
import { connect } from 'react-redux';
import { DateInput } from 'semantic-ui-calendar-react-17';
import { Button, Dropdown, Form, Grid, Header, Icon, Label, Message, Segment } from 'semantic-ui-react';
import { getDirectionVariantsForStop } from '../../../actions/gtfsStaticActions';
import { getRidershipStopActivityOverview, getRidershipStopActivityOverviewByStops } from '../../../actions/ridershipActions';
import { getStops as searchStops } from '../../../actions/runningTimeAnalyticsActions';
import { getStopsInAreaForMap } from '../../../actions/stopsActions';
import { AppState } from '../../../reducers';
import { getSelectedOrDefaultAgency } from '../../../selectors';
import type { DirectionVariant } from '../../../types/gtfsTypes';
import { PolylineProps } from '../../../types/MapModels';
import type { RidershipStopAggRecord, RidershipStopAreaLocation, RidershipStopDirectionSummaryRecord } from '../../../types/ridershipTypes';
import Utils from '../../../utilities/utils';
import type { DayTypeFilter } from '../../../utils/dayType';
import { dayTypeOptions as sharedDayTypeOptions, normalizeDayTypeFilter } from '../../../utils/dayType';
import { getRidershipDateOnlyDaysAgo } from '../../../utils/ridershipDate';
import AzureMapSelectionMap from '../../shared/AzureMap/AzureMapSelectionMap';
import RidershipMapLegend from '../common/RidershipMapLegend';
import { buildBubblePinsFromStopActivity } from '../common/ridershipMapUtils';
import { StopSelect } from '../common/RidershipSelectors';
import { projectAggregateMetrics, toRidershipCsvInteger } from '../common/ridershipUtils';
import StopPopupContent from '../common/StopPopupContent';

const colorPalette = ['#0d9afa', '#e74c3c', '#27ae60', '#8e44ad', '#f39c12', '#16a085', '#c0392b', '#2980b9'];

const styles = {
    date: {
        display: 'inline-block',
        width: '100%',
    },
};

function isAbortError(error: any) {
    return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
}

const normalizeRouteKey = (value?: string) => {
    const normalized = (value ?? '').trim().toLowerCase();
    return normalized || 'unknown route';
};

const normalizeRouteKeyFromSummary = (row: RidershipStopDirectionSummaryRecord) =>
    normalizeRouteKey(row.routeId ?? row.routeShortName);

type StopSummaryAggregator = {
    stopId: string;
    stopName: string;
    routeId: string;
    routeShortName: string;
    boardingSum: number;
    boardingAvgTotal: number;
    boardingAvgCount: number;
    alightingSum: number;
    alightingAvgTotal: number;
    alightingAvgCount: number;
    loadSum: number;
    loadAvgTotal: number;
    loadAvgCount: number;
};

const aggregateStopSummariesByRoute = (rows: RidershipStopDirectionSummaryRecord[] | null | undefined): RidershipStopDirectionSummaryRecord[] => {
    const grouped = new Map<string, StopSummaryAggregator>();

    (rows || []).forEach(row => {
        const displayRoute = (row.routeId ?? row.routeShortName ?? '').trim();
        const routeKey = normalizeRouteKey(displayRoute);
        const routeShortName = displayRoute || 'Unknown route';
        const current = grouped.get(routeKey) ?? {
            stopId: String(row.stopId ?? ''),
            stopName: String(row.stopName ?? ''),
            routeId: row.routeId ?? '',
            routeShortName,
            boardingSum: 0,
            boardingAvgTotal: 0,
            boardingAvgCount: 0,
            alightingSum: 0,
            alightingAvgTotal: 0,
            alightingAvgCount: 0,
            loadSum: 0,
            loadAvgTotal: 0,
            loadAvgCount: 0,
        };

        current.stopId = current.stopId || String(row.stopId ?? '');
        current.stopName = current.stopName || String(row.stopName ?? '');
        current.routeId = current.routeId || (row.routeId ?? '');

        const boardingSum = row.boardingSum ?? row.boarding ?? 0;
        const alightingSum = row.alightingSum ?? row.alighting ?? 0;
        const loadSum = row.loadSum ?? row.load ?? 0;
        current.boardingSum += boardingSum;
        current.alightingSum += alightingSum;
        current.loadSum += loadSum;

        const boardingAvg = row.boardingAvg ?? row.boarding;
        const alightingAvg = row.alightingAvg ?? row.alighting;
        const loadAvg = row.loadAvg ?? row.load;
        if (boardingAvg !== undefined && boardingAvg !== null) {
            current.boardingAvgTotal += boardingAvg;
            current.boardingAvgCount += 1;
        }
        if (alightingAvg !== undefined && alightingAvg !== null) {
            current.alightingAvgTotal += alightingAvg;
            current.alightingAvgCount += 1;
        }
        if (loadAvg !== undefined && loadAvg !== null) {
            current.loadAvgTotal += loadAvg;
            current.loadAvgCount += 1;
        }

        grouped.set(routeKey, current);
    });

    return Array.from(grouped.values()).map(item => ({
        stopId: item.stopId,
        stopName: item.stopName,
        routeId: item.routeId,
        routeShortName: item.routeShortName,
        boarding: item.boardingSum,
        boardingSum: item.boardingSum,
        boardingAvg: item.boardingAvgCount > 0 ? item.boardingAvgTotal / item.boardingAvgCount : undefined,
        alighting: item.alightingSum,
        alightingSum: item.alightingSum,
        alightingAvg: item.alightingAvgCount > 0 ? item.alightingAvgTotal / item.alightingAvgCount : undefined,
        load: item.loadSum,
        loadSum: item.loadSum,
        loadAvg: item.loadAvgCount > 0 ? item.loadAvgTotal / item.loadAvgCount : undefined,
    }));
};

interface Props {
    agency: ReturnType<typeof getSelectedOrDefaultAgency>;
}

interface StopOption {
    id: string;
    name: string;
    displayName: string;
}

type SelectionArea = {
    bounds: {
        north: number;
        south: number;
        east: number;
        west: number;
    } | null;
    geometry: {
        type: string;
        coordinates: any;
    } | null;
};

const metersToMiles = (meters: number) => meters / 1609.34;

const getRadiusMilesFromBounds = (bounds: SelectionArea['bounds']): number => {
    if (!bounds) return 0;
    const centerLat = (bounds.north + bounds.south) / 2;
    const centerLon = (bounds.east + bounds.west) / 2;
    const corners: [number, number][] = [
        [bounds.north, bounds.west],
        [bounds.north, bounds.east],
        [bounds.south, bounds.west],
        [bounds.south, bounds.east],
    ];
    const center = [centerLat, centerLon] as any;
    const distances = corners.map(corner => Utils.getDistanceMeters(center, corner as any));
    const maxMeters = distances.length ? Math.max(...distances) : 0;
    return metersToMiles(maxMeters);
};

const getBoundsFromGeometry = (geometry: SelectionArea['geometry']): SelectionArea['bounds'] => {
    if (!geometry?.coordinates) {
        return null;
    }
    let bounds: SelectionArea['bounds'] = null;
    const addCoord = (coord: any) => {
        if (!Array.isArray(coord) || coord.length < 2) {
            return;
        }
        const lon = Number(coord[0]);
        const lat = Number(coord[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
            return;
        }
        if (!bounds) {
            bounds = { north: lat, south: lat, east: lon, west: lon };
            return;
        }
        bounds = {
            north: Math.max(bounds.north, lat),
            south: Math.min(bounds.south, lat),
            east: Math.max(bounds.east, lon),
            west: Math.min(bounds.west, lon),
        };
    };
    const walk = (coords: any) => {
        if (!Array.isArray(coords)) {
            return;
        }
        if (coords.length > 0 && typeof coords[0] === 'number') {
            addCoord(coords);
            return;
        }
        coords.forEach(walk);
    };
    walk(geometry.coordinates);
    return bounds;
};

const RidershipStopSummaryPage: React.FC<Props> = (props: Props) => {
    const [stopOptions, setStopOptions] = React.useState<StopOption[]>([]);
    const [stopSearchQuery, setStopSearchQuery] = React.useState<string>('');
    const [stopSearchMessage, setStopSearchMessage] = React.useState<string | undefined>(undefined);
    const [stopLoading, setStopLoading] = React.useState<boolean>(false);
    const [selectedStop, setSelectedStop] = React.useState<StopOption | null>(null);
    const [appliedStop, setAppliedStop] = React.useState<StopOption | null>(null);
    const stopSearchTokenRef = React.useRef<number>(0);
    const selectedStopRef = React.useRef<StopOption | null>(null);
    const [areaStops, setAreaStops] = React.useState<RidershipStopAreaLocation[]>([]);
    const [areaStopsLoading, setAreaStopsLoading] = React.useState<boolean>(false);
    const areaStopsRequestRef = React.useRef<number>(0);
    const [areaSummaryRows, setAreaSummaryRows] = React.useState<RidershipStopDirectionSummaryRecord[]>([]);
    const [areaDirectionVariants, setAreaDirectionVariants] = React.useState<DirectionVariant[]>([]);
    const [areaSummaryLoading, setAreaSummaryLoading] = React.useState<boolean>(false);
    const areaSummaryRequestRef = React.useRef<number>(0);
    const [areaStopActivityRows, setAreaStopActivityRows] = React.useState<RidershipStopAggRecord[]>([]);
    const [areaStopActivityLoading, setAreaStopActivityLoading] = React.useState<boolean>(false);
    const areaStopActivityRequestRef = React.useRef<number>(0);
    const areaOverviewAbortRef = React.useRef<AbortController | null>(null);
    const [selectionResetToken, setSelectionResetToken] = React.useState<number>(0);

    const defaultFrom = getRidershipDateOnlyDaysAgo(30);
    const defaultTo = getRidershipDateOnlyDaysAgo(0);

    const [fromDate, setFromDate] = React.useState<string>(defaultFrom);
    const [toDate, setToDate] = React.useState<string>(defaultTo);
    const [dayType, setDayType] = React.useState<DayTypeFilter>('workdays');
    const [appliedFromDate, setAppliedFromDate] = React.useState<string>(defaultFrom);
    const [appliedToDate, setAppliedToDate] = React.useState<string>(defaultTo);
    const [appliedDayType, setAppliedDayType] = React.useState<DayTypeFilter>('workdays');
    const [hasApplied, setHasApplied] = React.useState<boolean>(false);
    const [aggregation, setAggregation] = React.useState<'sum' | 'avg'>('sum');
    const numberFormatter = React.useMemo(() => new Intl.NumberFormat('en-US'), []);
    const [selectedStopPopup, setSelectedStopPopup] = React.useState<{
        stopId?: string;
        stopName?: string;
        boarding?: number;
        alighting?: number;
        load?: number;
        lat?: number;
        lon?: number;
    } | null>(null);

    const dayTypeOptions = React.useMemo(() => sharedDayTypeOptions, []);

    const aggregationOptions = React.useMemo(() => ([
        {
            key: 'sum',
            value: 'sum',
            text: 'Sum',
        },
        {
            key: 'avg',
            value: 'avg',
            text: 'Avg',
        },
    ]), []);

    const [directionVariants, setDirectionVariants] = React.useState<DirectionVariant[]>([]);
    const [summaryRows, setSummaryRows] = React.useState<RidershipStopDirectionSummaryRecord[]>([]);
    const [loadingSummary, setLoadingSummary] = React.useState<boolean>(false);
    const [loadingVariants, setLoadingVariants] = React.useState<boolean>(false);
    const summaryAbortRef = React.useRef<AbortController | null>(null);
    const summaryRequestRef = React.useRef<number>(0);
    const variantsRequestRef = React.useRef<number>(0);
    const csvLinkRef = React.useRef<any>(null);
    const stopCsvLinkRef = React.useRef<any>(null);

    const isAreaSelectionActive = areaStops.length > 0;
    const reportLoading = loadingSummary || loadingVariants || areaSummaryLoading || areaStopActivityLoading;
    const activeSummaryRows = React.useMemo(() => (isAreaSelectionActive ? areaSummaryRows : summaryRows), [isAreaSelectionActive, areaSummaryRows, summaryRows]);
    const activeDirectionVariants = React.useMemo(() => (isAreaSelectionActive ? areaDirectionVariants : directionVariants), [isAreaSelectionActive, areaDirectionVariants, directionVariants]);

    // Track which routes are hidden on the map (toggled via color legend)
    const [hiddenRouteIds, setHiddenRouteIds] = React.useState<Set<string>>(new Set());
    const [showRoutesOnMap, setShowRoutesOnMap] = React.useState<boolean>(true);
    const [showColorLegendOnMap, setShowColorLegendOnMap] = React.useState<boolean>(true);
    const toggleRouteVisibility = React.useCallback((routeKey: string) => {
        setHiddenRouteIds(prev => {
            const next = new Set(prev);
            if (next.has(routeKey)) next.delete(routeKey);
            else next.add(routeKey);
            return next;
        });
    }, []);

    React.useEffect(() => {
        selectedStopRef.current = selectedStop;
    }, [selectedStop]);

    const handleCancel = React.useCallback(() => {
        summaryAbortRef.current?.abort();
        areaOverviewAbortRef.current?.abort();
        summaryRequestRef.current = Date.now();
        variantsRequestRef.current = Date.now();
        areaSummaryRequestRef.current = Date.now();
        areaStopActivityRequestRef.current = Date.now();
        setLoadingSummary(false);
        setLoadingVariants(false);
        setAreaSummaryLoading(false);
        setAreaStopActivityLoading(false);
    }, []);

    const handleSelectionChanged = React.useCallback(async (features: any[]) => {
        if (!features || features.length === 0) {
            setAreaStops([]);
            setAreaStopsLoading(false);
            return;
        }

        const geometry = (features[0] as any)?.geometry;
        const geometryType = String(geometry?.type || '');
        if (!geometry || geometryType.indexOf('Polygon') < 0) {
            setAreaStops([]);
            setAreaStopsLoading(false);
            return;
        }

        const bounds = getBoundsFromGeometry(geometry);
        if (!bounds) {
            setAreaStops([]);
            setAreaStopsLoading(false);
            return;
        }

        const agencyId = props.agency?.id;
        if (!agencyId) {
            return;
        }

        const centerLat = (bounds.north + bounds.south) / 2;
        const centerLon = (bounds.east + bounds.west) / 2;
        const radiusMiles = getRadiusMilesFromBounds(bounds);
        if (!Number.isFinite(radiusMiles) || radiusMiles <= 0) {
            setAreaStops([]);
            return;
        }

        const requestToken = Date.now();
        areaStopsRequestRef.current = requestToken;
        setAreaStopsLoading(true);

        try {
            const stops = await getStopsInAreaForMap(agencyId, centerLat, centerLon, radiusMiles);
            if (areaStopsRequestRef.current !== requestToken) {
                return;
            }

            const points = stops.map((stop, idx) => ({
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [stop.longitude, stop.latitude],
                },
                properties: { __index: idx },
            }));

            const polygonFeature = {
                type: 'Feature',
                geometry,
                properties: {},
            };

            const within = pointsWithinPolygonFn(
                { type: 'FeatureCollection', features: points } as any,
                polygonFeature as any,
            );

            const selectedStops = (within?.features || [])
                .map((feature: any) => stops[feature?.properties?.__index])
                .filter(Boolean);

            setAreaStops(selectedStops);
        } catch {
            if (areaStopsRequestRef.current === requestToken) {
                setAreaStops([]);
            }
        } finally {
            if (areaStopsRequestRef.current === requestToken) {
                setAreaStopsLoading(false);
            }
        }
    }, [props.agency?.id]);

    const handleClearSelection = React.useCallback(() => {
        areaStopsRequestRef.current = Date.now();
        setAreaStops([]);
        setAreaStopsLoading(false);
        areaSummaryRequestRef.current = Date.now();
        setAreaSummaryRows([]);
        setAreaDirectionVariants([]);
        setAreaSummaryLoading(false);
        areaStopActivityRequestRef.current = Date.now();
        setAreaStopActivityRows([]);
        setAreaStopActivityLoading(false);
        setSelectionResetToken(prev => prev + 1);
    }, []);

    const handleStopSearchChange = React.useCallback(async (rawQuery: string) => {
        const query = (rawQuery ?? '').trim();
        setStopSearchQuery(query);
        const agencyId = props.agency?.id;
        if (!agencyId || query.length < 2) {
            const current = selectedStopRef.current;
            setStopOptions(current ? [current] : []);
            setStopSearchMessage(undefined);
            setStopLoading(false);
            return;
        }
        const token = Date.now();
        stopSearchTokenRef.current = token;
        setStopLoading(true);
        try {
            const stops = await searchStops(agencyId, query);
            if (stopSearchTokenRef.current !== token) return;
            const list = stops || [];
            const messageEntry = list.length === 1 && !list[0].key ? list[0] : undefined;
            if (messageEntry) {
                setStopSearchMessage(messageEntry.value || 'No stops were found');
                const current = selectedStopRef.current;
                setStopOptions(current ? [current] : []);
                return;
            }
            const options = list.map(s => ({
                id: s.key,
                name: s.value,
                displayName: s.value,
            }));
            const current = selectedStopRef.current;
            if (current && !options.some(o => o.id === current.id)) {
                options.unshift(current);
            }
            setStopSearchMessage(undefined);
            setStopOptions(options);
        } finally {
            if (stopSearchTokenRef.current === token) {
                setStopLoading(false);
            }
        }
    }, [props.agency?.id]);

    const handleStopSelect = React.useCallback((stop: StopOption | null) => {
        if (!stop) {
            setStopSearchQuery('');
            setStopSearchMessage(undefined);
            setSelectedStop(null);
            setAppliedStop(null);
            setHasApplied(false);
            setDirectionVariants([]);
            setSummaryRows([]);
            return;
        }
        setSelectedStop(stop);
        setStopOptions(prev => {
            if (prev.some(o => o.id === stop.id)) {
                return prev;
            }
            return [stop, ...prev];
        });
        setStopSearchQuery('');
            setStopSearchMessage(undefined);
    }, []);

    const handleApply = React.useCallback(() => {
        if (!selectedStop) {
            setAppliedStop(null);
            setHasApplied(false);
            setSummaryRows([]);
            setDirectionVariants([]);
            return;
        }
        setAppliedStop(selectedStop);
        setAppliedFromDate(fromDate);
        setAppliedToDate(toDate);
        setAppliedDayType(dayType);
        setHasApplied(true);
        setSummaryRows([]);
    }, [selectedStop, fromDate, toDate, dayType]);

    React.useEffect(() => {
        const agencyId = props.agency?.id;
        const stopIds = areaStops.map(stop => stop.stopId).filter(Boolean);
        if (!agencyId || stopIds.length === 0) {
            const now = Date.now();
            areaSummaryRequestRef.current = now;
            setAreaSummaryRows([]);
            setAreaDirectionVariants([]);
            setAreaSummaryLoading(false);

            areaStopActivityRequestRef.current = now;
            setAreaStopActivityRows([]);
            setAreaStopActivityLoading(false);
            return;
        }

        const requestToken = Date.now();
        areaSummaryRequestRef.current = requestToken;
        areaStopActivityRequestRef.current = requestToken;
        areaOverviewAbortRef.current?.abort();
        const controller = new AbortController();
        areaOverviewAbortRef.current = controller;

        setAreaSummaryLoading(true);
        setAreaStopActivityLoading(true);

        setAreaSummaryRows([]);
        setAreaDirectionVariants([]);
        setAreaStopActivityRows([]);

        (async () => {
            try {
                const overview = await getRidershipStopActivityOverviewByStops(agencyId, stopIds, fromDate, toDate, dayType, undefined, undefined, { signal: controller.signal });
                if (areaSummaryRequestRef.current !== requestToken) {
                    return;
                }
                setAreaSummaryRows(overview?.summaries ?? []);
                setAreaDirectionVariants(overview?.directionVariants ?? []);
                setAreaStopActivityRows(overview?.stopActivity ?? []);
            } catch (error) {
                if (isAbortError(error) || controller.signal.aborted) {
                    return;
                }
                if (areaSummaryRequestRef.current === requestToken) {
                    setAreaSummaryRows([]);
                    setAreaDirectionVariants([]);
                    setAreaStopActivityRows([]);
                }
            } finally {
                if (areaSummaryRequestRef.current === requestToken) {
                    setAreaSummaryLoading(false);
                    setAreaStopActivityLoading(false);
                }
            }
        })();
        return () => {
            controller.abort();
        };
    }, [props.agency?.id, areaStops, fromDate, toDate, dayType]);

    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (isAreaSelectionActive) {
            setSummaryRows([]);
            return;
        }
        if (!agencyId || !appliedStop || !hasApplied) {
            setSummaryRows([]);
            return;
        }
        const requestToken = Date.now();
        summaryRequestRef.current = requestToken;
        summaryAbortRef.current?.abort();
        const controller = new AbortController();
        summaryAbortRef.current = controller;
        (async () => {
            try {
                setLoadingSummary(true);
                const overview = await getRidershipStopActivityOverview(agencyId, appliedStop.id, appliedFromDate, appliedToDate, appliedDayType, undefined, undefined, { signal: controller.signal });
                if (summaryRequestRef.current !== requestToken || controller.signal.aborted) return;
                setSummaryRows(overview?.summaries ?? []);
                const canonicalName = (overview?.stopName ?? '').trim();
                if (canonicalName && selectedStopRef.current && canonicalName !== selectedStopRef.current.name) {
                    const updatedStop = {
                        ...selectedStopRef.current,
                        name: canonicalName,
                        displayName: canonicalName,
                    };
                    selectedStopRef.current = updatedStop;
                    setSelectedStop(updatedStop);
                    setStopOptions(prev => prev.map(option => option.id === updatedStop.id ? updatedStop : option));
                }
            } catch (error) {
                if (isAbortError(error) || controller.signal.aborted) {
                    return;
                }
                throw error;
            } finally {
                if (summaryRequestRef.current === requestToken) setLoadingSummary(false);
            }
        })();
        return () => {
            controller.abort();
        };
    }, [props.agency?.id, appliedStop, appliedFromDate, appliedToDate, appliedDayType, hasApplied, isAreaSelectionActive]);

    // Load direction variants separately when applied stop changes
    React.useEffect(() => {
        const agencyId = props.agency?.id;
        if (isAreaSelectionActive) {
            setDirectionVariants([]);
            return;
        }
        if (!agencyId || !appliedStop?.id || !hasApplied) {
            setDirectionVariants([]);
            return;
        }
        const requestToken = Date.now();
        variantsRequestRef.current = requestToken;
        (async () => {
            try {
                setLoadingVariants(true);
                const result = await getDirectionVariantsForStop(agencyId, `Ho414_4620_${appliedStop.id}`);
                if (variantsRequestRef.current !== requestToken) return;
                const variants = result ?? [];
                setDirectionVariants(Array.isArray(variants) ? variants : []);
            } catch {
                if (variantsRequestRef.current === requestToken) setDirectionVariants([]);
            } finally {
                if (variantsRequestRef.current === requestToken) setLoadingVariants(false);
            }
        })();
    }, [props.agency?.id, appliedStop?.id, hasApplied, isAreaSelectionActive]);

    const directionLabelByInternalId = React.useMemo(() => {
        const map = new Map<string, string>();
        for (const variant of activeDirectionVariants) {
            const key = variant.directionVariantInternalId || variant.directionVariantId;
            if (!key) continue;
            const label = variant.directionVariantName
                || [variant.cardinalDirection, variant.lastStop].filter(Boolean).join(' → ')
                || key;
            map.set(key, label);
        }
        return map;
    }, [activeDirectionVariants]);

    const aggregatedSummaryRows = React.useMemo(() => aggregateStopSummariesByRoute(activeSummaryRows), [activeSummaryRows]);
    const projectedSummaryRows = React.useMemo(() => projectAggregateMetrics(aggregatedSummaryRows, aggregation), [aggregatedSummaryRows, aggregation]);

    const colorByRoute = React.useMemo(() => {
        const map = new Map<string, string>();
        aggregatedSummaryRows.forEach((row, idx) => {
            const color = colorPalette[idx % colorPalette.length];
            map.set(normalizeRouteKeyFromSummary(row), color);
        });
        return map;
    }, [aggregatedSummaryRows]);

    const summaryLookupByRoute = React.useMemo(() => {
        const map = new Map<string, RidershipStopDirectionSummaryRecord>();
        projectedSummaryRows.forEach(row => {
            map.set(normalizeRouteKeyFromSummary(row), row);
        });
        return map;
    }, [projectedSummaryRows]);

    const toNumber = React.useCallback((value: any): number | undefined => {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        if (typeof value === 'string' && value.trim().length > 0) {
            const parsed = Number(value);
            if (Number.isFinite(parsed)) return parsed;
        }
        return undefined;
    }, []);

    const routePolylines = React.useMemo<PolylineProps[]>(() => {
        return activeDirectionVariants
            .map((variant) => {
                const routeKey = normalizeRouteKey(variant.routeName);
                if (!summaryLookupByRoute.has(routeKey)) return null;
                if (hiddenRouteIds.has(routeKey)) return null;
                const coords = (variant.pointCoords || [])
                    .map(([lat, lon]) => {
                        const latNum = toNumber(lat);
                        const lonNum = toNumber(lon);
                        if (latNum === undefined || lonNum === undefined) return null;
                        return [latNum, lonNum] as [number, number];
                    })
                    .filter((point): point is [number, number] => !!point);
                if (coords.length === 0) return null;
                const color = colorByRoute.get(routeKey) || '#026db5';
                const directionLabel = variant.directionVariantInternalId
                    ? (directionLabelByInternalId.get(variant.directionVariantInternalId) ?? variant.directionVariantInternalId)
                    : undefined;
                const summary = summaryLookupByRoute.get(routeKey);
                const routeLabelValue = summary?.routeId || summary?.routeShortName || (variant.routeName ?? '').trim() || 'Unknown route';
                const routeLabel = [routeLabelValue, directionLabel].filter(Boolean).join(' • ') || routeLabelValue;
                const aggregationLabel = aggregation === 'avg' ? 'Average' : 'Sum';
                return {
                    locations: coords,
                    options: {
                        strokeColor: color,
                        strokeThickness: 7,
                    },
                    metadata: {
                        routeLabel,
                        aggregation: aggregationLabel,
                        boarding: summary?.boarding ?? summary?.boardingSum ?? summary?.boardingAvg ?? null,
                        alighting: summary?.alighting ?? summary?.alightingSum ?? summary?.alightingAvg ?? null,
                        load: summary?.load ?? summary?.loadSum ?? summary?.loadAvg ?? null,
                    },
                } as unknown as PolylineProps;
            })
            .filter((p): p is PolylineProps => !!p);
    }, [activeDirectionVariants, colorByRoute, toNumber, hiddenRouteIds, directionLabelByInternalId, summaryLookupByRoute, aggregation]);

    // Reset hidden routes when stop changes or new variants are loaded
    React.useEffect(() => {
        setHiddenRouteIds(new Set());
    }, [appliedStop?.id, isAreaSelectionActive, areaStops.length]);

    const stopCoordinate = React.useMemo(() => {
        if (!appliedStop) return null;
        for (const variant of activeDirectionVariants) {
            const stops = variant.stops || [];
            for (const stop of stops) {
                const stopData = stop as any;
                const stopId = (stopData.stopCode ?? '').toString();
                if (stopId !== appliedStop.id) continue;
                const lat = toNumber(stopData.lat ?? stopData.Lat ?? stopData.latitude ?? stopData.Latitude);
                const lon = toNumber(stopData.lon ?? stopData.Lon ?? stopData.longitude ?? stopData.Longitude);
                if (lat !== undefined && lon !== undefined) {
                    return {
                        lat,
                        lon,
                    };
                }
            }
        }

        // Fallback to first point from the first polyline if stop coordinates are missing
        const firstLine = activeDirectionVariants.find(dv => (dv.pointCoords?.length ?? 0) > 0);
        const firstPoint = firstLine?.pointCoords?.[0];
        if (firstPoint) {
            const lat = toNumber(firstPoint[0]);
            const lon = toNumber(firstPoint[1]);
            if (lat !== undefined && lon !== undefined) {
                return {
                    lat,
                    lon,
                };
            }
        }

        return null;
    }, [appliedStop, activeDirectionVariants, toNumber]);

    const projectedStopActivityRows = React.useMemo(() => projectAggregateMetrics(areaStopActivityRows, aggregation), [areaStopActivityRows, aggregation]);

    const selectedStopTotals = React.useMemo(() => {
        if (!appliedStop || !stopCoordinate || isAreaSelectionActive) {
            return null;
        }
        const rows = projectAggregateMetrics(summaryRows, aggregation);
        if (!rows.length) {
            return null;
        }
        const totals = rows.reduce((acc, row) => {
            acc.boarding += Number(row.boarding) || 0;
            acc.alighting += Number(row.alighting) || 0;
            acc.load += Number(row.load) || 0;
            return acc;
        }, { boarding: 0, alighting: 0, load: 0 });
        return {
            stopId: appliedStop.id,
            stopName: appliedStop.name,
            ...totals,
            lat: stopCoordinate.lat,
            lon: stopCoordinate.lon,
        };
    }, [appliedStop, stopCoordinate, isAreaSelectionActive, summaryRows, aggregation]);

    const areaBubblePins = React.useMemo(() => {
        if (!isAreaSelectionActive) {
            return { loadPins: [], alightPins: [], boardPins: [] };
        }
        return buildBubblePinsFromStopActivity(areaStops, projectedStopActivityRows);
    }, [isAreaSelectionActive, areaStops, projectedStopActivityRows]);

    const selectedStopBubblePins = React.useMemo(() => {
        if (isAreaSelectionActive || !selectedStopTotals) {
            return { loadPins: [], alightPins: [], boardPins: [] };
        }
        const stopLocation = {
            stopId: selectedStopTotals.stopId ?? '',
            stopName: selectedStopTotals.stopName ?? '',
            stopCode: selectedStopTotals.stopId ?? '',
            latitude: selectedStopTotals.lat as any,
            longitude: selectedStopTotals.lon as any,
        };
        const row = {
            stopId: selectedStopTotals.stopId ?? '',
            stopName: selectedStopTotals.stopName ?? '',
            boarding: selectedStopTotals.boarding,
            alighting: selectedStopTotals.alighting,
            load: selectedStopTotals.load,
        } as RidershipStopAggRecord;
        return buildBubblePinsFromStopActivity([stopLocation], [row]);
    }, [isAreaSelectionActive, selectedStopTotals]);

    const mapBubblePins = React.useMemo(() => {
        return isAreaSelectionActive ? areaBubblePins : selectedStopBubblePins;
    }, [isAreaSelectionActive, areaBubblePins, selectedStopBubblePins]);


    React.useEffect(() => {
        setSelectedStopPopup(null);
    }, [appliedStop?.id, selectionResetToken]);

    const popupInfoboxes = React.useMemo(() => {
        if (!selectedStopPopup || typeof selectedStopPopup.lat !== 'number' || typeof selectedStopPopup.lon !== 'number') {
            return [];
        }
        const stopLabel = selectedStopPopup.stopName || selectedStopPopup.stopId || 'Stop';
        const title = selectedStopPopup.stopName && selectedStopPopup.stopId
            ? `${selectedStopPopup.stopName} (${selectedStopPopup.stopId})`
            : stopLabel;
        return [{
            location: [selectedStopPopup.lat, selectedStopPopup.lon] as [number, number],
            title,
            text: (
                <StopPopupContent
                    stopId={selectedStopPopup.stopId}
                    stopName={selectedStopPopup.stopName}
                    boarding={selectedStopPopup.boarding}
                    alighting={selectedStopPopup.alighting}
                    load={selectedStopPopup.load}
                    formatNumber={(v) => numberFormatter.format(Utils.roundNumber(v ?? 0, 0))}
                />
            ),
            options: { showCloseButton: true, pixelOffset: [0, -15] },
        }];
    }, [selectedStopPopup, numberFormatter]);

    const mapOptions = React.useMemo(() => {
        const center = stopCoordinate
            ? [stopCoordinate.lon, stopCoordinate.lat] as [number, number]
            : (() => {
                const firstPolyline = routePolylines[0];
                const firstCoord = firstPolyline?.locations?.[0];
                if (!firstCoord) return undefined;
                const [lat, lon] = firstCoord;
                if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
                return [lon, lat] as [number, number];
            })();

        if (!center) return undefined;
        return {
            center,
            zoom: 13,
        };
    }, [stopCoordinate, routePolylines]);

    const chartHeight = React.useMemo(() => {
        if (!projectedSummaryRows.length) return 0;
        const rowHeight = 24;
        const minHeight = 260;
        const maxHeight = 1000;
        return Math.min(Math.max(projectedSummaryRows.length * rowHeight, minHeight), maxHeight);
    }, [projectedSummaryRows]);
    const chartViewportHeight = 520;

    const chartData = React.useMemo(() => {
        const labels = projectedSummaryRows.map(row => row.routeId || row.routeShortName || 'Route');
        return {
            labels,
            datasets: [
                {
                    label: 'Boardings',
                    backgroundColor: 'rgba(46, 204, 113, 0.85)',
                    borderColor: '#27ae60',
                    borderWidth: 1,
                    data: projectedSummaryRows.map(r => r.boarding),
                },
                {
                    label: 'Alightings',
                    backgroundColor: 'rgba(243, 156, 18, 0.85)',
                    borderColor: '#d35400',
                    borderWidth: 1,
                    data: projectedSummaryRows.map(r => r.alighting),
                },
                {
                    label: 'Load',
                    backgroundColor: 'rgba(52, 152, 219, 0.85)',
                    borderColor: '#2980b9',
                    borderWidth: 1,
                    data: projectedSummaryRows.map(r => r.load),
                },
            ],
        };
    }, [projectedSummaryRows]);


    const sortedStopActivityRows = React.useMemo(() => {
        return [...projectedStopActivityRows].sort((a, b) => (b.boarding ?? 0) - (a.boarding ?? 0));
    }, [projectedStopActivityRows]);

    const stopChartMaxHeight = 520;

    const stopChartHeight = React.useMemo(() => {
        if (!sortedStopActivityRows.length) return 0;
        const rowHeight = 18;
        const minHeight = 220;
        const maxHeight = stopChartMaxHeight;
        return Math.min(Math.max(sortedStopActivityRows.length * rowHeight, minHeight), maxHeight);
    }, [sortedStopActivityRows]);

    const stopChartData = React.useMemo(() => {
        const labels = sortedStopActivityRows.map(row => row.stopName || row.stopId || 'Stop');
        return {
            labels,
            datasets: [
                {
                    label: 'Boardings',
                    backgroundColor: 'rgba(46, 204, 113, 0.85)',
                    borderColor: '#27ae60',
                    borderWidth: 1,
                    data: sortedStopActivityRows.map(r => r.boarding),
                },
                {
                    label: 'Alightings',
                    backgroundColor: 'rgba(243, 156, 18, 0.85)',
                    borderColor: '#d35400',
                    borderWidth: 1,
                    data: sortedStopActivityRows.map(r => r.alighting),
                },
                {
                    label: 'Load',
                    backgroundColor: 'rgba(52, 152, 219, 0.85)',
                    borderColor: '#2980b9',
                    borderWidth: 1,
                    data: sortedStopActivityRows.map(r => r.load),
                },
            ],
        };
    }, [sortedStopActivityRows]);

    const stopChartOptions = React.useMemo<ChartOptions<'bar'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                stacked: true,
                ticks: {
                    maxRotation: 90,
                    minRotation: 90,
                    autoSkip: false,
                    callback: (_value: string | number, index: number) => {
                        const label = sortedStopActivityRows[index]?.stopName; // || sortedStopActivityRows[index]?.stopId || '';
                        if (!label) return '';
                        return label.length > 40 ? `${label.slice(0, 37)}...` : label;
                    },
                },
            },
            y: {
                stacked: false,
                beginAtZero: true,
            },
        },
        plugins: {
            legend: { position: 'top' },
            datalabels: { display: false },
            tooltip: {
                mode: 'x' as const,
                intersect: false,
                caretPadding: 110,
                titleFont: { size: 16 },
                bodyFont: { size: 15 },
                callbacks: {
                    title: function () {
                        return `Activity by Stop`;
                    },
                    label: function (tooltipItem: TooltipItem<'bar'>) {
                        const datasetLabel = tooltipItem.dataset?.label || 'Value';
                        return ` ${datasetLabel}: ${Utils.roundNumber(tooltipItem.parsed.y as number, 1)}`;
                    },
                },
            },
            filler: { propagate: true },
        },
    }), [sortedStopActivityRows]);

    const csvHeaders = React.useMemo(() => ([
        { label: 'Route', key: 'route' },
        { label: 'Boardings', key: 'boarding' },
        { label: 'Alightings', key: 'alighting' },
        { label: 'Load', key: 'load' },
        { label: 'Stop ID', key: 'stopId' },
        { label: 'Stop Name', key: 'stopName' },
        { label: 'From Date', key: 'fromDate' },
        { label: 'To Date', key: 'toDate' },
        { label: 'Day Type', key: 'dayType' },
        { label: 'Aggregation', key: 'aggregation' },
    ]), []);

    const csvRows = React.useMemo(() => {
        const stopId = isAreaSelectionActive ? 'Area selection' : (appliedStop?.id ?? '');
        const stopName = isAreaSelectionActive ? 'Selected area' : (appliedStop?.name ?? '');
        const fromValue = isAreaSelectionActive ? fromDate : appliedFromDate;
        const toValue = isAreaSelectionActive ? toDate : appliedToDate;
        const dayValue = isAreaSelectionActive ? dayType : appliedDayType;
        return projectedSummaryRows.map(row => ({
            route: row.routeId ?? row.routeShortName ?? '',
            boarding: toRidershipCsvInteger(row.boarding),
            alighting: toRidershipCsvInteger(row.alighting),
            load: toRidershipCsvInteger(row.load),
            stopId,
            stopName,
            fromDate: fromValue,
            toDate: toValue,
            dayType: dayValue,
            aggregation,
        }));
    }, [projectedSummaryRows, isAreaSelectionActive, appliedStop, appliedFromDate, appliedToDate, appliedDayType, fromDate, toDate, dayType, aggregation]);

    const csvFileName = React.useMemo(() => {
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        const stopPart = isAreaSelectionActive ? '_area' : (appliedStop?.id ? `_stop_${appliedStop.id}` : '');
        return `ridership_stop_summary${stopPart}_${timestamp}.csv`;
    }, [appliedStop?.id, isAreaSelectionActive]);

    const csvDisabled = loadingSummary || areaSummaryLoading || !csvRows.length;

    const stopCsvHeaders = React.useMemo(() => ([
        { label: 'Stop ID', key: 'stopId' },
        { label: 'Stop Name', key: 'stopName' },
        { label: 'Boardings', key: 'boarding' },
        { label: 'Alightings', key: 'alighting' },
        { label: 'Load', key: 'load' },
        { label: 'From Date', key: 'fromDate' },
        { label: 'To Date', key: 'toDate' },
        { label: 'Day Type', key: 'dayType' },
        { label: 'Aggregation', key: 'aggregation' },
    ]), []);

    const stopCsvRows = React.useMemo(() => {
        const fromValue = isAreaSelectionActive ? fromDate : appliedFromDate;
        const toValue = isAreaSelectionActive ? toDate : appliedToDate;
        const dayValue = isAreaSelectionActive ? dayType : appliedDayType;
        return sortedStopActivityRows.map(row => ({
            stopId: row.stopId ?? '',
            stopName: row.stopName ?? '',
            boarding: toRidershipCsvInteger(row.boarding),
            alighting: toRidershipCsvInteger(row.alighting),
            load: toRidershipCsvInteger(row.load),
            fromDate: fromValue,
            toDate: toValue,
            dayType: dayValue,
            aggregation,
        }));
    }, [sortedStopActivityRows, isAreaSelectionActive, fromDate, toDate, appliedFromDate, appliedToDate, dayType, appliedDayType, aggregation]);

    const stopCsvFileName = React.useMemo(() => {
        const timestamp = moment(new Date()).format('YYYY_MM_DD');
        const stopPart = isAreaSelectionActive ? '_area_stops' : '_stops';
        return `ridership_stop_activity${stopPart}_${timestamp}.csv`;
    }, [isAreaSelectionActive]);

    const stopCsvDisabled = areaStopActivityLoading || loadingSummary || !sortedStopActivityRows.length;

    const newLegendClickHandler = React.useCallback((_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>) => {
        const index = legendItem.datasetIndex ?? 0;
        const type = legend.chart.config;

        console.log(type, legendItem.datasetIndex);
        // you can also get dataset of your selected element
        console.log(chartData.datasets[index]);
        const ci = legend.chart;
        if (ci.isDatasetVisible(index)) {
            ci.hide(index);
            legendItem.hidden = true;
        } else {
            ci.show(index);
            legendItem.hidden = false;
        }
    }, [chartData]);

    // Chart options with y-axis label (tick) colors synchronized to colorPalette by direction
    const chartOptions = React.useMemo<ChartOptions<'bar'>>(() => ({
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y',
        scales: {
            x: {
                stacked: false,
                beginAtZero: true,
                ticks: {
                    maxRotation: 90,
                    minRotation: 90,
                    autoSkip: false,
                },
            },
            y: {
                stacked: true,
                title: { display: false },
                ticks: {
                    // Color each Y tick (row label) to match its route color on the map
                    color: (ctx: any) => {
                        const idx = ctx.index ?? 0;
                        const row = projectedSummaryRows[idx];
                        const routeKey = normalizeRouteKeyFromSummary(row);
                        const color = (routeKey && colorByRoute.get(routeKey)) || '#000';
                        return color;
                    },
                    callback: function (value: string | number) {
                        const label = typeof this.getLabelForValue === 'function'
                            ? this.getLabelForValue(value as any)
                            : value?.toString();
                        if (typeof label !== 'string') return label;
                        return label.length > 60 ? `${label.slice(0, 57)}...` : label;
                    },
                },
            },
        },
        onClick: function (evt, element) {
            if (element.length > 0 && evt) {
                console.log(element, element[0].datasetIndex);
                // you can also get dataset of your selected element
                console.log(chartData.datasets[element[0].datasetIndex]);
            }
        },
        plugins: {
            legend: {
                position: 'top',
                onClick: newLegendClickHandler,
            },
            datalabels: { display: false },
            tooltip: {
                mode: 'y' as const,
                intersect: false,
                caretPadding: 110,
                titleFont: { size: 16 },
                bodyFont: { size: 15 },
                callbacks: {
                    title: function () {
                        return `Activity by Route`;
                    },
                    label: function (tooltipItem: TooltipItem<'bar'>) {
                        let lineType = ' ';
                        if (tooltipItem.datasetIndex === 0)
                            lineType = 'Boarding';
                        else if (tooltipItem.datasetIndex === 1)
                            lineType = 'Alightings';
                        else if (tooltipItem.datasetIndex === 2)
                            lineType = 'Load';
                        return ` ${lineType}: ${Utils.roundNumber(tooltipItem.parsed.x as number, 1)}`;
                    },
                    labelColor: function (tooltipItem: TooltipItem<'bar'>) {
                        if (tooltipItem.datasetIndex === 0) {
                            return {
                                borderColor: '#2ECC71FF',
                                backgroundColor: '#2ECC71FF',
                            };
                        } else if (tooltipItem.datasetIndex === 1) {
                            return {
                                borderColor: '#F39C12FF',
                                backgroundColor: '#F39C12FF',
                            };
                        } else if (tooltipItem.datasetIndex === 2) {
                            return {
                                borderColor: '#2980B9FF',
                                backgroundColor: '#2980B9FF',
                            };
                        } else {
                            return {
                                borderColor: '#FF8368',
                                backgroundColor: '#FF8368',
                            };
                        }
                    },
                },
            },
            filler: { propagate: true },
        },
    }), [projectedSummaryRows, colorByRoute, chartData, aggregation, newLegendClickHandler]);

    const colorLegend = React.useMemo(() => {
        return aggregatedSummaryRows.map((row, idx) => {
            const routeKey = normalizeRouteKeyFromSummary(row);
            const color = colorByRoute.get(routeKey) ?? colorPalette[idx % colorPalette.length];
            const isHidden = hiddenRouteIds.has(routeKey);
            return (
                <Label
                    as="a"
                    key={routeKey || idx}
                    onClick={() => toggleRouteVisibility(routeKey)}
                    title={isHidden ? 'Show on map' : 'Hide from map'}
                    style={{
                        width: '100%',
                        backgroundColor: color,
                        color: '#fff',
                        marginBottom: 6,
                        cursor: 'pointer',
                        opacity: isHidden ? 0.35 : 1,
                        textDecoration: isHidden ? 'line-through' : undefined,
                    }}
                >
                    {row.routeId || row.routeShortName || 'Route'}
                </Label>
            );
        });
    }, [aggregatedSummaryRows, hiddenRouteIds, toggleRouteVisibility, colorByRoute]);

    const handleFromDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        setFromDate(value);
    };

    const handleToDateChange = (_event: React.SyntheticEvent<HTMLElement, Event>, { value }: { value: string }) => {
        setToDate(value);
    };

    const noStopSelected = !selectedStop && !isAreaSelectionActive;
    const noData = (!isAreaSelectionActive && appliedStop && summaryRows.length === 0 && hasApplied && !loadingSummary)
        || (isAreaSelectionActive && areaSummaryRows.length === 0 && !areaSummaryLoading);
    const noDataMessage = isAreaSelectionActive
        ? 'No ridership data found for the selected area and date range.'
        : 'No ridership data found for the selected stop and date range.';

    return (
        <Segment basic style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
        }}>
            <Header as="h1" className="reportHeader">Ridership Stop Activity Overview</Header>
            <div style={{
                flex: 1,
                marginTop: 16,
            }}>
                <Form>
                    <Form.Group widths="equal" inline className="inputGroup">
                        <Form.Field>
                            <label className="inputLabel">Stop</label>
                            <StopSelect
                                className="stops-list ui dropdown"
                                items={stopOptions}
                                value={selectedStop}
                                clearable
                                onChanged={handleStopSelect}
                                loading={stopLoading}
                                onSearchChanged={handleStopSearchChange}
                                searchQuery={stopSearchQuery}
                                placeholder="Search for a stop"
                                noResultsMessage={stopSearchMessage ?? (stopSearchQuery.length < 2 ? 'Enter at least 2 characters' : 'No stops found')}
                            />
                        </Form.Field>
                        <Form.Field className="calendarField">
                            <label className="calendarInputLabel">From date</label>
                            <DateInput
                                className="bph-date"
                                name="stopSummaryFromDate"
                                dateFormat="YYYY-MM-DD"
                                placeholder="Select date"
                                value={fromDate}
                                iconPosition="left"
                                popupPosition="bottom center"
                                closable={true}
                                startMode="day"
                                preserveViewMode={false}
                                animation="fade"
                                disableMinute={false}
                                style={styles.date}
                                onChange={handleFromDateChange}
                                maxDate={new Date()}
                            />
                        </Form.Field>
                        <Form.Field className="calendarField">
                            <label className="calendarInputLabel">To date</label>
                            <DateInput
                                className="bph-date"
                                name="stopSummaryToDate"
                                dateFormat="YYYY-MM-DD"
                                placeholder="Select date"
                                value={toDate}
                                iconPosition="left"
                                popupPosition="bottom center"
                                closable={true}
                                startMode="day"
                                preserveViewMode={false}
                                animation="fade"
                                disableMinute={false}
                                style={styles.date}
                                onChange={handleToDateChange}
                                maxDate={new Date()}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label className="inputLabel">Day Type</label>
                            <Dropdown
                                className="ui dropdown"
                                selection
                                fluid
                                options={dayTypeOptions}
                                value={dayType}
                                onChange={(_, d) => setDayType(normalizeDayTypeFilter(d.value as DayTypeFilter))}
                            />
                        </Form.Field>
                        <Form.Field>
                            <label className="inputLabel">Aggregation</label>
                            <Dropdown
                                selection
                                fluid
                                options={aggregationOptions}
                                value={aggregation}
                                onChange={(_, d) => setAggregation((d.value as 'sum' | 'avg') || 'sum')}
                            />
                        </Form.Field>
                        <Form.Field>
                            {reportLoading
                                ? (
                                    <Button className="fieldControl"
                                            color="red"
                                            content="Cancel"
                                            onClick={handleCancel}/>
                                )
                                : (
                                    <Button className="primaryButton fieldControl"
                                            content="Apply"
                                            onClick={handleApply}
                                            disabled={stopLoading || (!selectedStop && areaStops.length === 0)}/>
                                )}
                        </Form.Field>
                    </Form.Group>
            </Form>

                {noStopSelected && (
                    <Message info content="Select a stop to view ridership activity." style={{ marginTop: 16 }}/>
                )}
                {noData && (
                    <Message warning content={noDataMessage}
                             style={{ marginTop: 16 }}/>
                )}

                <BlockUi blocking={loadingSummary || loadingVariants || areaSummaryLoading || areaStopActivityLoading} message="Loading stop activity..." style={{
                    marginTop: 16,
                }}>
                <Grid>
                    <Grid.Row>
                        <Grid.Column>
                            <Segment>
                                <div style={{
                                    width: '100%',
                                    position: 'relative',
                                }}>
                                    <div style={{
                                        backgroundColor: 'rgb(249, 250, 251)',
                                        border: '1px solid rgba(0,0,0,0.08)',
                                        borderRadius: 6,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 4,
                                        padding: 8,
                                        margin: 4,
                                        position: 'absolute',
                                        zIndex: 2,
                                        maxHeight: 590,
                                        overflowY: 'auto',
                                        overflowX: 'hidden',
                                        right: 8,
                                        left: 'auto',
                                        top: 45,
                                    }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                                            <Form.Checkbox
                                                label="Show routes"
                                                checked={showRoutesOnMap}
                                                onChange={(_e, data) => setShowRoutesOnMap(Boolean(data.checked))}
                                            />
                                            <Form.Checkbox
                                                label="Show color legend"
                                                checked={showColorLegendOnMap}
                                                onChange={(_e, data) => setShowColorLegendOnMap(Boolean(data.checked))}
                                            />
                                        </div>
                                        {showColorLegendOnMap && colorLegend}
                                        {areaStopsLoading && (
                                            <Label basic color="grey" size="tiny" style={{ marginTop: 6 }}>
                                                Loading selected stops...
                                            </Label>
                                        )}
                                        {(areaStops.length > 0 || areaStopsLoading) && (
                                            <Button
                                                basic
                                                size="mini"
                                                onClick={handleClearSelection}
                                                style={{ marginTop: 6 }}
                                            >
                                                Clear selection
                                            </Button>
                                        )}
                                    </div>
                                    <div style={{
                                        width: '100%',
                                        height: 600,
                                    }}>
                                        <AzureMapSelectionMap
                                            map={mapOptions}
                                            showZoomControls={true}
                                            polylinesProp={showRoutesOnMap ? routePolylines : []}
                                            loadBubblePins={mapBubblePins.loadPins}
                                            alightingsBubblePins={mapBubblePins.alightPins}
                                            boardBubblePins={mapBubblePins.boardPins}
                                            showRouteSegmentPopup={showRoutesOnMap}
                                            showStopHoverPopup={false}
                                            infoboxesProp={popupInfoboxes}
                                            disableAutoCamera={true}
                                            onSelectionChanged={handleSelectionChanged}
                                            onStopClick={(info) => setSelectedStopPopup({
                                                stopId: info.stopId,
                                                stopName: info.stopName,
                                                boarding: info.boarding,
                                                alighting: info.alighting,
                                                load: info.load,
                                                lat: info.lat,
                                                lon: info.lon,
                                            })}
                                            selectionResetToken={selectionResetToken}
                                            emitSelectionArea={true}
                                        />
                                    </div>
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                                        <RidershipMapLegend mapMode="bubble" />
                                        <Label color="blue" style={{ marginLeft: 'auto' }}>
                                            Stops: {numberFormatter.format(areaStops.length)}
                                        </Label>
                                    </div>
                                </div>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                    {isAreaSelectionActive && (
                        <Grid.Row>
                            <Grid.Column style={{
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                    <CSVLink
                                        headers={stopCsvHeaders}
                                        data={stopCsvRows}
                                        filename={stopCsvFileName}
                                        target="_blank"
                                        className="hidden"
                                        ref={stopCsvLinkRef as any}
                                    />
                                    <Button
                                        basic
                                        color="grey"
                                        size="small"
                                        icon
                                        labelPosition="left"
                                        loading={areaStopActivityLoading || loadingSummary}
                                        disabled={stopCsvDisabled}
                                        onClick={() => {
                                            stopCsvLinkRef.current?.link?.click();
                                        }}
                                    >
                                        <Icon name="download" />
                                        Download CSV
                                    </Button>
                                </div>
                                <Segment style={{ flex: 1 }}>
                                    {areaStopActivityRows.length === 0 && !areaStopActivityLoading && (
                                        <Message warning content="No ridership data found for the selected stops."
                                                 style={{ marginBottom: 16 }}/>
                                    )}
                                    {areaStopActivityRows.length > 0 && (
                                        <div style={{ height: stopChartHeight }}>
                                            <Bar data={stopChartData} options={stopChartOptions} plugins={[ChartDataLabels]}/>
                                        </div>
                                    )}
                                </Segment>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                    <Grid.Row>
                        <Grid.Column style={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                <CSVLink
                                    headers={csvHeaders}
                                    data={csvRows}
                                    filename={csvFileName}
                                    target="_blank"
                                    className="hidden"
                                    ref={csvLinkRef as any}
                                />
                                <Button
                                    basic
                                    color="grey"
                                    size="small"
                                    icon
                                    labelPosition="left"
                                    loading={loadingSummary || areaSummaryLoading}
                                    disabled={csvDisabled}
                                    onClick={() => {
                                        csvLinkRef.current?.link?.click();
                                    }}
                                >
                                    <Icon name="download" />
                                    Download CSV
                                </Button>
                            </div>
                            <Segment style={{ flex: 1 }}>
                                <div style={{ maxHeight: chartViewportHeight, overflowY: 'auto', paddingRight: 6 }}>
                                    <div style={{ height: chartHeight }}>
                                        <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]}/>
                                    </div>
                                </div>
                            </Segment>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
                </BlockUi>
            </div>
        </Segment>
    );
};

const mapStateToProps = (state: AppState) => ({
    agency: getSelectedOrDefaultAgency(state),
});

export default connect(mapStateToProps)(RidershipStopSummaryPage);
